<template>
  <div
    @keyup.esc="close"
    tabindex="0"
    v-if="edit"
    class="flex application flex-col w-full overflow-y-auto px-3 lg:px-0 lg:w-[900px] relative items-center outline-none"
  >
    <Suspense>
      <template #default>
        <component
          :is="currentComponent"
          :application="application.app_id"
          :key="item.id"
          :isLoading="isLoading"
          :error="err"
          @answer="updateAnswer"
          @next="next"
          @prev="prev"
          @close="close"
          @skip="skip"
          :item="item"
        />
      </template>
      <template #fallback>
        <div class="flex flex-col justify-center items-center w-full mt-64">
          <Spinner class="w-12 h-12" text-color="text-fire" />
        </div>
      </template>
    </Suspense>
  </div>
  <div
    v-else
    @keyup.esc="close"
    tabindex="0"
    class="w-full lg:w-full 2xl:w-full outline-none"
  >
    <!-- Application not Submitted -->
    <div
      v-if="!submitted"
      @keyup.esc="close"
      tabindex="0"
      class="dashboard-panel outline-none"
    >
      <div class="dashboard-panel-inner">
        <div class="flex flex-row justify-between">
          <div>
            <h2 class="heading">{{ t("dashboard.loan-app") }}</h2>
            <p class="sub-heading">
              &nbsp;<!--{{ t("dashboard.mapping-sub") }}-->
            </p>
          </div>
          <p class="font-bold text-sm text-vulcan dark:text-white">
            # {{ application.app_id }}
          </p>
        </div>
        <div class="flex flex-col mt-6">
          <div class="dashboard-panel-progress">
            <span
              class="dashboard-panel-progress-bar"
              :style="styleObject"
            ></span>
          </div>
          <div class="mt-3 text-right">
            <h3
              class="font-medium md:font-bold text-sm md:text-xl text-vulcan dark:text-white w-full"
            >
              {{ stats.progress.percent }}% {{ t("complete") }}
            </h3>
          </div>
        </div>
        <div
          class="flex flex-col md:flex-row justify-start md:justify-between mt-8 w-full items-start md:items-center"
        >
          <div class="flex flex-col space-y-4 pb-8 md:pb-0">
            <div class="flex flex-row items-center space-x-2 cursor-pointer">
              <svg class="dashboard-panel-icon" viewBox="0 0 34 34">
                <g transform="translate(0 0)">
                  <path
                    id="Vector"
                    d="M16.646,21.958H5.313C1.884,21.958,0,20.074,0,16.646V5.313C0,1.884,1.884,0,5.313,0H12.4a1.07,1.07,0,0,1,1.063,1.062V16.646c0,2.238.949,3.188,3.188,3.188a1.063,1.063,0,0,1,0,2.125ZM5.313,2.125c-2.238,0-3.187.949-3.187,3.188V16.646c0,2.238.949,3.188,3.188,3.188h6.8a6.034,6.034,0,0,1-.779-3.187V2.125Z"
                    transform="translate(1.771 10.271)"
                  />
                  <path
                    id="Vector-2"
                    data-name="Vector"
                    d="M8.146,7.792H1.063A1.07,1.07,0,0,1,0,6.729V3.9A3.894,3.894,0,0,1,3.9,0H8.3a1.068,1.068,0,0,1,.836.411,1.081,1.081,0,0,1,.184.921,4.347,4.347,0,0,0-.113,1.147v4.25A1.07,1.07,0,0,1,8.146,7.792ZM2.125,5.667H7.083V2.125H3.9A1.771,1.771,0,0,0,2.125,3.9Z"
                    transform="translate(6.021 4.604)"
                  />
                  <path
                    id="Vector-3"
                    data-name="Vector"
                    d="M1.063,9.208A1.07,1.07,0,0,1,0,8.146V1.063A1.07,1.07,0,0,1,1.063,0,1.07,1.07,0,0,1,2.125,1.063V8.146A1.07,1.07,0,0,1,1.063,9.208Z"
                    transform="translate(18.771 10.271)"
                  />
                  <path
                    id="Vector-4"
                    data-name="Vector"
                    d="M1.063,9.208A1.07,1.07,0,0,1,0,8.146V1.063A1.07,1.07,0,0,1,1.063,0,1.07,1.07,0,0,1,2.125,1.063V8.146A1.07,1.07,0,0,1,1.063,9.208Z"
                    transform="translate(24.437 10.271)"
                  />
                  <path
                    id="Vector-5"
                    data-name="Vector"
                    d="M6.729,9.208H1.063A1.07,1.07,0,0,1,0,8.146V2.479A2.491,2.491,0,0,1,2.479,0H5.313A2.491,2.491,0,0,1,7.792,2.479V8.146A1.07,1.07,0,0,1,6.729,9.208Zm-4.6-2.125H5.667v-4.6a.351.351,0,0,0-.354-.354H2.479a.351.351,0,0,0-.354.354Z"
                    transform="translate(18.771 23.021)"
                  />
                  <path
                    id="Vector-6"
                    data-name="Vector"
                    d="M1.063,7.792A1.07,1.07,0,0,1,0,6.729V1.063A1.07,1.07,0,0,1,1.063,0,1.07,1.07,0,0,1,2.125,1.063V6.729A1.07,1.07,0,0,1,1.063,7.792Z"
                    transform="translate(7.437 17.354)"
                  />
                  <path
                    id="Vector-7"
                    data-name="Vector"
                    d="M13.813,30.458h-8.5C1.884,30.458,0,28.574,0,25.146V5.313C0,1.884,1.884,0,5.313,0h8.5c3.428,0,5.312,1.884,5.312,5.312V25.146C19.125,28.574,17.241,30.458,13.813,30.458ZM5.313,2.125c-2.238,0-3.187.949-3.187,3.188V25.146c0,2.238.949,3.188,3.188,3.188h8.5c2.238,0,3.188-.949,3.188-3.188V5.313c0-2.238-.949-3.187-3.187-3.187Z"
                    transform="translate(13.104 1.771)"
                  />
                  <path
                    id="Vector-8"
                    data-name="Vector"
                    d="M0,0H34V34H0Z"
                    fill="none"
                    opacity="0"
                  />
                </g>
              </svg>
              <div>
                <h3 class="font-bold text-sm text-vulcan dark:text-white">
                  {{ t("questions") }}
                </h3>
                <p class="text-mute text-sm">
                  {{ t("questionsTotal") }} -
                  {{ stats.questions.current }}
                  {{ t("dashboard.of") }}
                  {{ stats.questions.total }}
                  {{ t("questions") }}
                </p>
                <p class="text-mute text-sm">
                  {{ t("questionsRequired") }} -
                  {{ stats.questions.required.done }}
                  {{ t("dashboard.of") }}
                  {{ stats.questions.required.item }}
                  {{ t("questions") }}
                </p>
              </div>
            </div>
            <div class="flex items-start justify-start">
              <div
                @click.prevent="onToggle('showAllDocuments', true)"
                class="flex flex-row items-center space-x-2 cursor-pointer"
              >
                <svg
                  class="fill-current text-[#2c283e] dark:text-white"
                  width="34"
                  height="34"
                  viewBox="0 0 34 34"
                >
                  <g
                    id="Group_1409"
                    data-name="Group 1409"
                    transform="translate(0 0)"
                  >
                    <path
                      id="Path_1288"
                      data-name="Path 1288"
                      d="M33.7,8.575,25.3.287A1,1,0,0,0,24.6,0H4.969a1,1,0,0,0-1,1V8.63A5.262,5.262,0,0,0,0,13.725V24.183a5.262,5.262,0,0,0,3.973,5.1V33a1,1,0,0,0,1,1H33a1,1,0,0,0,1-1V9.284A1,1,0,0,0,33.7,8.575Zm-8.111-5.2,4.982,4.911H26.588a1,1,0,0,1-1-1ZM1.992,24.183V13.725a3.265,3.265,0,0,1,3.261-3.261H15.705a3.265,3.265,0,0,1,3.261,3.261V24.183a3.265,3.265,0,0,1-3.261,3.261H5.253a3.265,3.265,0,0,1-3.261-3.261Zm3.973,7.825V29.436h9.74a5.259,5.259,0,0,0,5.253-5.253V13.725a5.259,5.259,0,0,0-5.253-5.253H5.965V1.992H23.6v5.3a2.991,2.991,0,0,0,2.988,2.988h5.42V32.008Z"
                    />
                    <path
                      id="Path_1289"
                      data-name="Path 1289"
                      d="M98.278,202.933l1.37-1.37V208.8a1,1,0,0,0,1.992,0v-7.234l1.37,1.37a1,1,0,0,0,1.409-1.409l-3.07-3.07a1,1,0,0,0-1.409,0l-3.07,3.07a1,1,0,1,0,1.409,1.409Z"
                      transform="translate(-90.164 -185.005)"
                    />
                  </g>
                </svg>
                <div>
                  <h3 class="font-bold text-sm text-vulcan dark:text-white">
                    {{ t("dashboard.uploaded") }}
                  </h3>
                  <p class="text-mute text-sm">
                    {{ stats.files.current }}
                    {{ t("dashboard.of") }}
                    {{ stats.files.total }} {{ t("files") }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div
            class="flex flex-col flex-shrink-0 flex-none w-full md:w-[200px] items-center md:items-end"
          >
            <form
              method="POST"
              class="flex w-1/2 md:w-[200px] flex-col space-y-4"
              :action="`/applications/${application.app_id}`"
            >
              <input type="hidden" name="_token" :value="csrf" />
              <a
                v-if="stats.questions.current > 0"
                @click.prevent="onToggle('showAllQuestion', true)"
                class="cursor-pointer dashboard-panel-button-edit text-center"
                >{{ t("dashboard.edit-app") }}</a
              >
              <a
                v-if="stats.questions.current === 0"
                @click.prevent="onToggle('showAllQuestion', true)"
                class="cursor-pointer dashboard-panel-button-edit text-center"
                >{{ t("dashboard.start-app") }}</a
              >
              <button
                :disabled="!canSubmit"
                type="submit"
                class="dashboard-panel-button-submit"
              >
                {{ t("action.submit") }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Application Submitted -->
    <div v-if="submitted" class="flex flex-col space-y-12">
      <!-- Application Window -->
      <div class="w-full">
        <div class="dashboard-panel">
          <!-- Partners -->
          <div
            v-if="stats.status === 'rejected' || stats.status === 'declined'"
            class="dashboard-panel-inner"
          >
            <div
              class="flex flex-col md:flex-row justify-center md:justify-between mt-6 w-full items-start md:items-center space-y-4"
            >
              <div>
                <div class="flex flex-col">
                  <div>
                    <h2
                      class="font-medium text-xl text-vulcan dark:text-white border-b-2 pb-2"
                    >
                      {{ t("dashboard.process") }}
                    </h2>
                    <p class="text-vulcan dark:text-white w-full text-sm pt-2">
                      {{ stats.loan.message }}
                    </p>
                  </div>
                  <div class="w-full flex flex-col mt-[40px]">
                    <h2
                      class="font-medium text-xl text-vulcan dark:text-white border-b-2 pb-2"
                    >
                      {{ t("partner.heading") }}
                    </h2>
                    <div
                      class="items-center flex flex-col mt-2 text-vulcan dark:text-white"
                    >
                      <Partner
                        v-for="partner in partners"
                        :key="partner.id"
                        :item="partner"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="canEdit"
                class="flex flex-row flex-shrink-0 md:px-4 justify-end w-full md:w-auto mt-4"
              >
                <a
                  @click.prevent="onToggle('showAllQuestion', true)"
                  class="dashboard-panel-button-edit cursor-pointer"
                  >{{ t("dashboard.edit-app") }}</a
                >
              </div>
            </div>
          </div>

          <div v-else class="dashboard-panel-inner">
            <div class="flex flex-row justify-between">
              <h2 class="heading">
                {{ t("dashboard.loan-app") }}
              </h2>
              <p class="font-bold text-sm text-vulcan dark:text-white">
                Application# {{ stats.app_id }}
              </p>
            </div>
            <p class="sub-heading">
              {{
                t("dashboard.submitted") +
                " " +
                application.displaySubmittedDate
              }}
            </p>
            <div class="flex flex-row justify-between mt-6">
              <div class="dashboard-panel-progress">
                <span
                  class="dashboard-panel-progress-bar"
                  :style="styleProgressObject"
                ></span>
              </div>
            </div>
            <div
              class="flex flex-col md:flex-row justify-center md:justify-between mt-6 w-full items-start md:items-center space-y-4"
            >
              <div>
                <div class="flex flex-row items-center space-x-4">
                  <svg class="dashboard-panel-icon" viewBox="0 0 34 34">
                    <g
                      id="Group_1410"
                      data-name="Group 1410"
                      transform="translate(0 0)"
                    >
                      <path
                        id="Path_1285"
                        data-name="Path 1285"
                        d="M292.107,53l2.33,1.724a.348.348,0,0,1-.2.625l-.731,0h0a.343.343,0,0,1-.343-.34,1,1,0,0,0-1.992.012,2.338,2.338,0,0,0,1.7,2.232V57.3a1,1,0,1,0,1.992,0v-.05a2.336,2.336,0,0,0,1.707-2.266V54.98a2.346,2.346,0,0,0-.946-1.861l-2.33-1.723a.343.343,0,0,1,.2-.618h.732a.343.343,0,0,1,.343.342,1,1,0,1,0,1.992,0,2.338,2.338,0,0,0-1.7-2.247v-.048a1,1,0,1,0-1.992,0v.047A2.334,2.334,0,0,0,292.107,53Z"
                        transform="translate(-271.826 -44.654)"
                      />
                      <path
                        id="Path_1286"
                        data-name="Path 1286"
                        d="M213.594,16.822a8.411,8.411,0,1,0-8.413-8.411A8.421,8.421,0,0,0,213.594,16.822Zm0-14.83a6.419,6.419,0,1,1-6.421,6.419A6.427,6.427,0,0,1,213.594,1.992Z"
                        transform="translate(-191.556)"
                      />
                      <path
                        id="Path_1287"
                        data-name="Path 1287"
                        d="M33.315,261.925a3.249,3.249,0,0,0-4.422-.677l-3.79,2.535a3.123,3.123,0,0,0-2.751-1.649h-2.7a7.4,7.4,0,0,0-11.575,1.871l-.507.31-.131-.385a1,1,0,0,0-1.265-.62l-5.5,1.878a1,1,0,0,0-.62,1.265l3.325,9.726a1,1,0,0,0,1.265.62l5.5-1.878a1,1,0,0,0,.609-1.3l8.721-.27a11.324,11.324,0,0,0,6.587-2.109l6.576-4.678a3.249,3.249,0,0,0,.683-4.643ZM4.942,274.593l-2.681-7.841,3.611-1.234,2.681,7.841Zm26.536-9.648L24.9,269.623a9.344,9.344,0,0,1-5.448,1.741h-.031l-9.348.289-1.848-5.406,1.123-.687h0a1.006,1.006,0,0,0,.374-.411,5.41,5.41,0,0,1,8.79-1.335,1.013,1.013,0,0,0,.724.312h3.113a1.128,1.128,0,1,1,0,2.256h-5.62a1,1,0,0,0,0,1.992h5.62a3.124,3.124,0,0,0,3.033-2.383l4.623-3.093.017-.011a1.259,1.259,0,0,1,1.453,2.057Z"
                        transform="translate(0 -242.854)"
                      />
                    </g>
                  </svg>
                  <div>
                    <h3 class="font-bold text-sm text-vulcan dark:text-white">
                      {{ t("dashboard.process") }}
                    </h3>
                    <p class="text-mute w-full text-sm">
                      {{ stats.loan.message }}
                    </p>
                  </div>
                </div>
              </div>
              <div
                v-if="canEdit"
                class="flex flex-row flex-shrink-0 md:px-4 justify-end w-full md:w-auto mt-4"
              >
                <a
                  @click.prevent="onToggle('showAllQuestion', true)"
                  class="dashboard-panel-button-edit cursor-pointer"
                  >{{ t("dashboard.edit-app") }}</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SMS Notification -->
      <div
        v-if="
          stats.status !== 'withdrawn' &&
          stats.status !== 'rejected' &&
          stats.status !== 'declined'
        "
        class="w-full"
      >
        <SmsNotification @show="onUpdateSmsProcess" />
      </div>

      <!-- Communication Feed -->
      <div
        v-if="
          stats.status !== 'withdrawn' &&
          stats.status !== 'rejected' &&
          stats.status !== 'declined'
        "
        class="w-full"
      >
        <div class="dashboard-panel">
          <div class="dashboard-panel-inner">
            <h2 class="heading">{{ t("dashboard.comm") }}</h2>
            <p class="sub-heading"></p>
            <div
              class="flex w-full h-[235px] overflow-y-auto overflow-x-hidden pr-4 py-4"
            >
              <CommunicationFeed :application="application" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Consent Modal -->
  <ModalConsent @close="showConsent = false" :open="showConsent">
    <ApplicationConsent
      :consent="application.consent"
      :id="application.app_id"
      @close="toggleConsent"
    />
  </ModalConsent>

  <TransitionRoot
    as="template"
    :show="showAllQuestion"
    enter="transition transform ease-out duration-300"
    enter-from="opacity-0 -translate-x-1/2"
    enter-to="opacity-100 translate-x-0"
    leave="transition transform ease-in duration-600"
    leave-from="opacity-100 translate-x-0"
    leave-to="opacity-0 -translate-x-1/2"
  >
    <all-questions
      @selection="setItem"
      @close="showAllQuestion = false"
      :current="current"
      class="h-full absolute z-[999] bg-gradient-to-b from-[#f6f6f7] via-[#fafafa] to-[#f6f6f6] dark:from-[#22212a] dark:via-[#262630] dark:to-[#23212e] top-0 right-0 flex w-full md:w-[60%] lg:w-[45%] xl:w-[40%] min-h-screen px-4 md:px-8"
      :items.sync="items"
    />
  </TransitionRoot>
  <TransitionRoot
    as="template"
    :show="showAllDocuments"
    enter="transition transform ease-out duration-300"
    enter-from="opacity-0 -translate-x-1/2"
    enter-to="opacity-100 translate-x-0"
    leave="transition transform ease-in duration-600"
    leave-from="opacity-100 translate-x-0"
    leave-to="opacity-0 -translate-x-1/2"
  >
    <all-documents
      :id="application.app_id"
      @update="updateDoc"
      @selection="setItem"
      @close="showAllDocuments = false"
      class="h-full absolute z-[999] bg-gradient-to-b from-[#f6f6f7] via-[#fafafa] to-[#f6f6f6] dark:from-[#22212a] dark:via-[#262630] dark:to-[#23212e] top-0 right-0 flex w-full md:w-[60%] lg:w-[45%] xl:w-[40%] min-h-screen px-4 md:px-8"
      :items.sync="files"
    />
  </TransitionRoot>
  <TransitionRoot
    as="template"
    :show="showAllMessages"
    enter="transition transform ease-out duration-300"
    enter-from="opacity-0 -translate-x-1/2"
    enter-to="opacity-100 translate-x-0"
    leave="transition transform ease-in duration-600"
    leave-from="opacity-100 translate-x-0"
    leave-to="opacity-0 -translate-x-1/2"
  >
    <AllNotes
      :id="application.app_id"
      @update="updateMsg"
      @selection="setItem"
      @close="showAllMessages = false"
      class="h-full absolute z-[999] bg-gradient-to-b from-[#f6f6f7] via-[#fafafa] to-[#f6f6f6] dark:from-[#22212a] dark:via-[#262630] dark:to-[#23212e] top-0 right-0 flex w-full md:w-[60%] lg:w-[45%] xl:w-[40%] px-4 md:px-8"
      :items.sync="conversations"
    />
  </TransitionRoot>
  <FooterComponent :stats="stats" @toggle="onToggle" />
  <!--    <Notifications :type="notification.type" :message="notification.message" @show="notification.show = !notification.show" v-if="notification.show"/>-->
  <SmsProcess @close="onUpdateSmsProcess(false)" :open="showSmsProcess" />
</template>

<script>
import { useI18n } from "vue-i18n";
import { computed, onMounted, onUnmounted, reactive, ref, toRefs } from "vue";
import FooterComponent from "./FooterComponent";
import { TransitionRoot } from "@headlessui/vue";
import AllQuestions from "./AllQuestions";
import AllDocuments from "./AllDocuments";
import Spinner from "./Spinner";
import _ from "lodash";
import SmsNotification from "./SmsNotification";
import CommunicationFeed from "./CommunicationFeedComponent";
import AllNotes from "./AllNotes";
import Notifications from "./Notification";
import SmsProcess from "./SmsProcess";
import ApplicationConsent from "./ApplicationConsent";
import ModalConsent from "./ModalConsent";
import Partner from "./Partner";
import application from "./Application";

export default {
  name: "DashboardComponent",
  components: {
    Partner,
    ModalConsent,
    ApplicationConsent,
    SmsProcess,
    Notifications,
    AllNotes,
    CommunicationFeed,
    SmsNotification,
    FooterComponent,
    AllDocuments,
    AllQuestions,
    Spinner,
    TransitionRoot,
  },
  props: {
    submitted: {
      type: Boolean,
      default: false,
    },
    stats: {
      type: Object,
      default: {},
    },
    application: {
      type: Object,
      default: {},
    },
  },
  setup(props) {
    const { t } = useI18n();

    const canSubmit = computed(() => props.stats.canSubmit);

    const canEdit = computed(() => props.stats.canEdit);

    const csrf = computed(
      () => document.querySelector('meta[name="csrf-token"]').content
    );

    const state = reactive({
      showSmsProcess: false,
      notification: {
        show: false,
        message: "",
        type: "success",
      },
      edit: false,
      showAllQuestion: false,
      showAllDocuments: false,
      showAllMessages: false,
      current: 1,
      items: [],
      partners: [],
      files: [],
      conversations: [],
      isLoading: false,
      err: {},
    });

    const showConsent = ref(false);

    const item = ref({});

    const error = ref(null);

    const setItem = (obj, index) => {
      if (obj.value.has_extra) {
        item.value = obj.value.answer.answer.value;
        item.extra = obj.value.answer.answer.extra;
      } else {
        item.value = obj;
      }
      state.current = index;
      state.edit = true;
    };

    onMounted(async () => {
      window.addEventListener("keyup", (e) => {
        if (e.code === "Escape") {
          close();
        }
      });

      try {
        if (
          props.stats.status === "rejected" ||
          props.stats.status === "declined"
        ) {
          const p = await axios.get("/partners");
          state.partners = p.data;
        }

        const res = await axios.get(
          `/questions?application=${props.application.app_id}`
        );
        const force = 1;
        state.items = _.each(res.data, (obj, idx) => {
          obj.value = "";

          if (obj.has_extra) {
            if (!obj.answer) {
              obj.answer = {
                answer: { value: "", extra: [] },
              };
            }
          }
          if (obj.id === force) {
            state.current = force;
            item.value.current = true;
            item.value = obj;
          } else {
            item.value.current = false;
          }
        });

        await fetchFiles();
      } catch (err) {
      } finally {
        state.isLoading = false;
      }
      checkNotification();
    });

    onUnmounted(() => {});

    const onUpdateSmsProcess = (value) => {
      state.showSmsProcess = value;
    };

    const checkNotification = () => {};

    const fetchFiles = async () => {
      try {
        const res = await axios.get(
          `/applications/${props.application.app_id}/files`
        );
        state.files = res.data;
      } catch (err) {}
    };

    const fetchConversations = async () => {
      try {
        const res = await axios.get(
          `/applications/${props.application.app_id}/conversations`
        );
        state.conversations = res.data;
      } catch (err) {}
    };

    const checkConsent = async () => {
      try {
        const res = await axios.get(
          `/applications/${props.application.app_id}/consent`
        );
        return res.data.consent;
      } catch (e) {
        return false;
      }
    };

    const onToggle = async (type, value) => {
      if (type === "showAllQuestion") {
        if (!(await checkConsent())) {
          showConsent.value = true;
          return true;
        }

        /*const url = new URL(window.location);
        url.searchParams.set("edit", "true");
        window.history.pushState({}, "", url);*/

        state.showAllQuestion = true;
        resetSliderState("question");
      }
      if (type === "showAllDocuments") {
        await fetchFiles();
        state.showAllDocuments = true;
        //state.edit = false
        resetSliderState("doc");
      }
      if (type === "showAllMessages") {
        await fetchConversations();
        state.showAllMessages = true;
        //state.edit = false
        resetSliderState("msg");
      }
    };

    const resetSliderState = (current) => {
      switch (current) {
        case "doc":
          state.showAllQuestion = false;
          state.showAllMessages = false;
          break;
        case "question":
          state.showAllDocuments = false;
          state.showAllMessages = false;
          break;
        case "msg":
          state.showAllDocuments = false;
          state.showAllQuestion = false;
          break;
      }
    };

    const getProgress = (value) => {
      return value + "%";
    };

    const next = async (obj, selection, skip = false) => {
      try {
        if (!skip) {
          state.isLoading = true;
          await update(obj, selection);
        }

        state.current++;

        check();
        item.value = state.items[state.current];
      } catch (err) {
        state.err = err;
      } finally {
        state.isLoading = false;
      }
    };

    const prev = () => {
      console.log("PREV");
      state.current--;
      check();
      item.value = state.items[state.current];
    };

    const close = () => {
      window.location.href = `/applications/${props.application.app_id}`;
    };

    //Loan Progress before submission
    const styleObject = reactive({
      width: getProgress(props.stats.progress.bar),
    });

    //Loan Progress after submission
    const styleProgressObject = reactive({
      width: getProgress(props.stats.loan.progress),
    });

    const currentComponent = computed(() => {
      switch (item.value?.type ?? "exit") {
        case "range":
          return "Range";
        case "date":
          return "input-date";
        case "number":
          return "input-number";
        case "loan":
          return "input-loan";
        case "file":
          return "input-file";
        case "location":
          return "input-location";
        case "picture":
          return "input-picture";
        case "autocomplete":
          return "input-autocomplete";
        case "social":
          return "input-social";
        case "text":
          return "input-text";
        case "radio":
          return "input-radio";
        case "shareholders":
          return "input-shareholders";
        case "multi-select":
          return "input-multi";
        case "pdf_download_upload":
          return "input-fileio";
        case "exit":
          window.location.href = `/applications/${props.application.app_id}`;
          break;
        default:
          return "input-text";
      }
    });

    const update = async (question, selection) => {
      let data = {};
      if (question.has_extra) {
        data = {
          application: props.application.app_id,
          id: question.id,
          key: question.key,
          value: selection.value ?? selection,
          extra: selection.extra ?? {},
        };
      } else {
        data = {
          application: props.application.app_id,
          id: question.id,
          key: question.key,
          value: selection,
        };
      }
      try {
        const res = await axios.put("/answers", data);
        const objIndex = state.items.findIndex((obj) => obj.id === question.id);
        state.items[objIndex]["answer"] = res.data;
      } catch (err) {
        throw err;
      }
    };

    const updateAnswer = (answer) => {
      const objIndex = state.items.findIndex((obj) => obj.id === answer.id);
      state.items[objIndex] = answer;
      item.value = answer;
    };

    const updateDoc = async () => {
      await fetchFiles();
    };

    const updateMsg = async () => {
      await fetchConversations();
    };

    const check = () => {
      if (state.current === state.items.length) {
        window.location.href = "/dashboard";
      }
    };

    const skip = (obj) => {
      state.current++;
      check();
      item.value = state.items[state.current];
    };

    const toggleConsent = (action = false) => {
      showConsent.value = !showConsent.value;

      if (action && !showConsent.value) {
        state.showAllQuestion = true;
      }
    };

    return {
      ...toRefs(state),
      t,
      toggleConsent,
      showConsent,
      update,
      updateAnswer,
      skip,
      currentComponent,
      setItem,
      csrf,
      updateMsg,
      updateDoc,
      next,
      close,
      prev,
      item,
      error,
      canSubmit,
      canEdit,
      styleObject,
      styleProgressObject,
      onUpdateSmsProcess,
      onToggle,
    };
  },
};
</script>
